import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectEconomics } from '../reducers/economicsSlice';

import { selectUser } from '../reducers/userSlice';
import { selectOptions } from '../reducers/optionsSlice';
import { chartColors } from '../helpers/chartColors';
import { E3M3_TO_MCF, M3_TO_BBL, MONTH_LENGTH } from '../helpers/conversionFactors';

export const EconomicsChart = ({ cashflowType }) => {

  const user = useSelector(selectUser);
  const options = useSelector(selectOptions);
  const economics = useSelector(selectEconomics);
  const currentEconomics = economics?.byId?.[economics?.currentEconomicsId];

  const orientation = options?.chartOrientation;

  const data = useMemo(() => getChartData(currentEconomics, cashflowType), [currentEconomics, cashflowType]);

  const darkMode = user?.value?.darkMode;

  const layout = {
    title: {
      text: 'Cash Flow and Production Forecast',
      font: {
        size: 12
      }
    },
    grid: (orientation === 'Horizontal' ? {rows: 1, columns: 2, pattern: 'independent'} : {rows: 2, columns: 1, pattern: 'independent'}),
    barmode: 'stack',
    yaxis: { // Main y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: `Cash Flow ${currentEconomics?.settings?.currency === 'cad' ? '(CAD)' : '(USD)'}`,
        font: {
          size: 12
        }
      },
      tickfont: {size: 10},
      autorange: true,
      // rangemode: 'nonnegative',
      anchor: 'x',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0.55, 1])
    },
    yaxis2: { // Cum cash flow y axis
      showgrid: false, 
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      title: {
        text: `Cumulative Cash Flow ${currentEconomics?.settings?.currency === 'cad' ? '(CAD)' : '(USD)'}`,
        font: {
          size: 12
        }
      },
      tickfont: {size: 10},
      overlaying: 'y',
      side: 'right',
      autorange: true,
      // rangemode: 'nonnegative',
      anchor: 'x',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0.55, 1])
    },
    xaxis: {
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Date',
        font: {
          size: 12
        },
        standoff: 10
      },
      tickfont: {size: 10},
      type: 'date',
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      domain: (orientation === 'Horizontal' ? [0, 0.425] : [0, 1]),
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    yaxis3: { // Production y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: 'Production (boe/d)',
        font: {
          size: 12
        }
      },
      tickfont: {size: 10},
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x2',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0, 0.45])
    },
    yaxis4: { // Num wells y axis
      showgrid: false,
      color: darkMode ? 'lightgrey' : 'grey',
      linecolor: darkMode ? 'lightgrey' : 'grey',
      title: {
        text: 'Number of Wells',
        font: {
          size: 12
        }
      },
      tickfont: {size: 10},
      overlaying: 'y3',
      side: 'right',
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x2',
      domain: (orientation === 'Horizontal' ? [0, 1] : [0, 0.45])
    },
    xaxis2: {
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Date',
        standoff: 10,
        font: {size: 12}
      },
      tickfont: {size: 10},
      anchor: 'y3',
      type: 'date',
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      domain: (orientation === 'Horizontal' ? [0.575, 1] : [0, 1]),
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: orientation === 'Horizontal' ? -0.15 : -0.05
    },
    uirevision:'true',
    shapes: [ 
    ],
    margin: {"t": 50, "l": 50, "r": 50, "b": 50},
    images: [
      {
        x: 0.055,
        y: 1.02,
        sizex: 0.1,
        sizey: 0.075,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xanchor: "right",
        xref: "paper",
        yanchor: "bottom",
        yref: "paper",
        opacity: 1
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };


  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: currentEconomics?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 480,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', minHeight: '500px', minWidth: '600px'}}
    />
  );

};

function getChartData(economics, cashflowType) {
  try {
    let data = [];
    const currency = economics?.settings?.currency;
    const cashflow = cashflowType === 'net' ? economics?.cashflowNet : economics?.cashflowGross;
    if (cashflow?.length > 0) { // plot cashflow
      
      const lastYear = cashflow?.[cashflow?.length - 1]?.year;
      
      data = plotForecast(data, economics?.[cashflowType === 'net' ? 'forecastNet' : 'forecastGross'], lastYear, economics?.settings);
      data = plotNumWells(data, cashflow, lastYear);

      data = plotCashlow(data, cashflow, currency);
      data = plotCumCashlow(data, cashflow, currency);
    };
    return data;
  } catch (error) {
    console.log('Could not get chart data!');
    console.log(error);
    return null;
  };
};

function plotCashlow(data, economicsForecast, currency) {
  try {
    let X = [];
    let Y = [];
    let YATax = [];
    let newData = [...data];

    for (let entry of economicsForecast) { // plot monthly economics forecast
      X.push(entry.year);
      Y.push(entry.cashflowBTax);
      YATax.push(entry.cashflowATax);
    };

    newData.push({ // Plot well cash flow
      type: "scatter",
      mode: "line",
      x: X,
      y: Y,
      name: `Cash Flow b/ Tax ${currency === 'usd' ? '(USD)' : '(CAD)'}`,
      marker: {color: chartColors?.[0]}
      // marker: {color: '#33aa33'}
    });

    newData.push({ // Plot well cash flow a/ tax
      type: "scatter",
      mode: "line",
      x: X,
      y: YATax,
      name: `Cash Flow a/ Tax ${currency === 'usd' ? '(USD)' : '(CAD)'}`,
      marker: {color: chartColors?.[0]},
      line: {dash: 'dash'}
      // marker: {color: '#33aa33'}
    });
    return newData;
  } catch (error) {
    console.log(error);
    return data;
  };
};

function plotCumCashlow(data, economicsForecast, currency) {
  try {
    let X = [];
    let Y = [];
    let YATax = [];
    let newData = [...data];

    for (let entry of economicsForecast) { // plot cum cash flow
      X.push(entry.year);
      Y.push(entry.cumCashflowBTax);
      YATax.push(entry.cumCashflowATax);
    };

    newData.push({ // Plot well cum cash flow
      type: "scatter",
      mode: "line",
      yaxis: 'y2',
      x: X,
      y: Y,
      name: `Cum. Cash Flow b/ Tax ${currency === 'usd' ? '(USD)' : '(CAD)'}`,
      marker: {color: chartColors?.[7]}
    });

    newData.push({ // Plot well cum cash flow a/ tax
      type: "scatter",
      mode: "line",
      yaxis: 'y2',
      x: X,
      y: YATax,
      name: `Cum. Cash Flow a/ Tax ${currency === 'usd' ? '(USD)' : '(CAD)'}`,
      marker: {color: chartColors?.[7]},
      line: {dash: 'dash'}
    });
    return newData;
  } catch (error) {
    console.log(error);
    return data;
  };
};

function plotForecast(data, forecast, lastYear, economicsSettings) {
  try {
    let newData = [...data];
    if (!forecast) return newData;

    for (let fluid of forecast?.fluids?.filter(f => f !== 'gas')) {
      let  X = [];
      let  Y = [];

      for (let value of forecast?.byFluid?.[fluid]) { // plot annual economics forecast
        const volume = getBOEVolume(value.volume?.p50, fluid, economicsSettings) / 12 / MONTH_LENGTH; // convert annual volumes to daily
        if (value.year <= lastYear && value.numSources > 0) {
          X.push(value.year);
          Y.push(volume);
        };
      };

      newData.push({ // Plot fluid forecast
        type: "bar",
        x: X,
        y: Y,
        name: `${formatFluidName(fluid)} P50 (boe/d)`,
        yaxis: 'y3',
        xaxis: 'x2',
        marker: {
          color: fluid === 'oil' ? '#9DC209' : fluid === 'salesGas' ? '#DC381F' : fluid === 'ngl' ? '#ff615b' : fluid === 'condensate' ? '#FFCE44' : '#2B65EC'
        }
      });
    };


    return newData;
    
  } catch (error) {
    console.log(error);
    return data;
  };
};

function formatFluidName(fluid) {
  try {
    let formattedFluid = fluid;
    if (fluid === 'salesGas') {
      formattedFluid = 'Sales Gas';
    } else if (fluid === 'ngl') {
      formattedFluid = 'NGL';
    } else {
      formattedFluid = formattedFluid.charAt(0)?.toUpperCase()+formattedFluid.substring(1,);
    };

    return formattedFluid;

  } catch (error) {
    return fluid;
  };
};

function getBOEVolume(volume, fluid, economicsSettings) {
  try {
  
    switch(fluid) {
      case 'oil':
        return (volume * M3_TO_BBL) || 0;
      case 'gas':
        return (volume * E3M3_TO_MCF * (1 - economicsSettings?.nglGasShrinkage) / 6) || 0;
      case 'condensate':
        return (volume * M3_TO_BBL) || 0;
      case 'water':
        return (volume * M3_TO_BBL) || 0;
      default:
        return volume;
    };

  } catch (error) {
    console.log(error);
    return 0;
  };
};

function plotNumWells(data, economicsForecast, lastDate) {
  try {
    let newData = [...data];

    let X = [];
    let Y = [];

    for (let c of economicsForecast) {
      X.push(c?.year);
      Y.push(c?.numSources);
    };

    // for (let fluid of forecast?.fluids) {

    //   for (let value of forecast?.byFluid?.[fluid]) { // plot annual number of wells
    //     const date = new Date(value.date);
        
    //     if ((!lastDate || date <= lastDate)) {
    //       if (!X.find(x => x.getFullYear() === date.getFullYear() && x.getMonth() === date.getMonth())) {
    //         X.push(date);
    //         Y.push(value.numSources);
    //       } else {
    //         Y[X.findIndex(x => x.getFullYear() === date.getFullYear() && x.getMonth() === date.getMonth())] = Math.max(Y[X.findIndex(x => x.getFullYear() === date.getFullYear() && x.getMonth() === date.getMonth())], value.numSources);
    //       };
    //     };
    //   };
    // };

    newData.push({ // Plot fluid production
      type: "scatter",
      mode: "line",
      x: X,
      y: Y,
      name: `Number of Wells`,
      yaxis: 'y4',
      xaxis: 'x2',
      marker: {
        color: '#333333'
      }
    });


    return newData;
    
  } catch (error) {
    console.log(error);
    return data;
  };
};
